// How old the data on a page is: the backend's own timestamps for the cached Microsoft reads (/api/sync-status, the same
// ones "Datakilder svarer" shows), never a guess. A page says how old the OLDEST of the reads behind it is, so data is
// never made to look fresher than it is.
import { useEffect, useState } from 'react';
import { apiFetch } from '../api';

export function ageText(ms) {
  const mins = Math.max(0, Math.floor(ms / 60000));
  if (mins < 1) return 'under 1 min';
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return mins % 60 === 0 || hours >= 3 ? `${hours} ${hours === 1 ? 'time' : 'timer'}` : `${hours} ${hours === 1 ? 'time' : 'timer'} ${mins % 60} min`;
  const days = Math.floor(hours / 24);
  return `${days} ${days === 1 ? 'dag' : 'dage'}`;
}

// { ms, tone, rows } for the given routes (all of them when no list is given), or null when none has been fetched yet.
// tone: ok under an hour (the backend refreshes hourly), age under a day, old beyond.
export function freshnessOf(routes, keys, now) {
  if (!Array.isArray(routes)) return null;
  const rows = routes.filter((r) => (!Array.isArray(keys) || keys.includes(r.routeKey)) && r.hasData && r.syncedAt).map((r) => ({ label: r.label, ms: now - new Date(r.syncedAt).getTime() }));
  if (rows.length === 0) return null;
  const ms = Math.max(...rows.map((r) => r.ms));
  return { ms, tone: ms < 3600000 ? 'ok' : ms < 86400000 ? 'age' : 'old', rows: rows.sort((a, b) => b.ms - a.ms) };
}

// The backend's list of cached reads for a tenant, re-read every five minutes (the text keeps counting in between).
export function useSyncStatus(tid, { poll = true } = {}) {
  const [routes, setRoutes] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [nextManualSyncAt, setNextManualSyncAt] = useState(null);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    if (!tid) return undefined;
    let cancelled = false;
    const load = () => apiFetch(`/api/sync-status?tenantId=${tid}`)
      .then((r) => r.json())
      .then((d) => { if (!cancelled && Array.isArray(d.routes)) { setRoutes(d.routes); setNextManualSyncAt(d.nextManualSyncAt || null); setLastSyncAt(d.lastSyncAt || null); setNow(Date.now()); } })
      .catch(() => {});
    load();
    const timer = poll ? setInterval(load, 300000) : null;
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [tid, reloadKey, poll]);
  return { routes, now, nextManualSyncAt, lastSyncAt, reload: () => setReloadKey((k) => k + 1) };
}
